import React, { useState } from 'react';
import { Product, ProductImage } from './index';
import { validateImageUrl, normalizeImageUrl } from '../../utils/imageUrlValidators';
import { categoryService } from '../../services/categoryService';
import './ShopBackOffice.css';

const ProductForm: React.FC<{
  productForm: Partial<Product>;
  handleFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  productImages: ProductImage[];
  handleUpdateImage: (index: number, field: keyof ProductImage, value: string) => void;
  handleRemoveImage: (index: number) => void;
  setMainImage: (index: number) => void;
  addImageUrl: (url: string, alt: string) => void;
}> = ({
  productForm,
  handleFormChange,
  productImages,
  handleUpdateImage,
  handleRemoveImage,
  setMainImage,
  addImageUrl
}) => {
  const [newImageUrl, setNewImageUrl] = useState<string>('');
  const [newImageAlt, setNewImageAlt] = useState<string>('');
  const [showHtmlInfo, setShowHtmlInfo] = useState<boolean>(false);

  // Function to validate the video URL
  const isValidVideoUrl = (url: string): boolean => {
    if (!url) return true; // Empty is valid (optional field)

    // Check if it's a valid URL
    try {
      new URL(url);
    } catch (_) {
      return false;
    }

    // Check if it's a video URL from common platforms or has a video extension
    return (
      url.includes('youtube.com') ||
      url.includes('youtu.be') ||
      url.includes('vimeo.com') ||
      url.includes('facebook.com/watch') ||
      url.match(/\.(mp4|webm|ogg)$/i) !== null
    );
  };

  // Function to validate image URL
  const isValidImageUrl = (url: string): boolean => {
    return validateImageUrl(url);
  };

  // Handle adding a new image URL
  const handleAddImageUrl = () => {
    if (isValidImageUrl(newImageUrl)) {
      // Normalize the URL to handle special cases
      const normalizedUrl = normalizeImageUrl(newImageUrl);
      addImageUrl(normalizedUrl, newImageAlt || 'Product image');
      setNewImageUrl('');
      setNewImageAlt('');
    } else {
      alert('Please enter a valid image URL');
    }
  };

  // Toggle HTML info panel
  const toggleHtmlInfo = () => {
    setShowHtmlInfo(!showHtmlInfo);
  };

  // Get all categories for rendering select options
  const categories = categoryService.getAllCategories('en');

  return (
    <div className="product-form-container">
      <h3>Product Information</h3>

      <form className="product-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="titleEn">Title (English) *</label>
            <input
              type="text"
              id="titleEn"
              name="titleEn"
              value={productForm.titleEn || ''}
              onChange={handleFormChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="titleAr">Title (Arabic) *</label>
            <input
              type="text"
              id="titleAr"
              name="titleAr"
              value={productForm.titleAr || ''}
              onChange={handleFormChange}
              required
              dir="rtl"
            />
          </div>

          <div className="form-group">
            <label htmlFor="price">Price (₪) *</label>
            <input
              type="number"
              id="price"
              name="price"
              value={productForm.price || ''}
              onChange={handleFormChange}
              required
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={productForm.category || ''}
              onChange={handleFormChange}
              required
            >
              <option value="">Select Category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="ageRangeEn">Age Range (English)</label>
            <input
              type="text"
              id="ageRangeEn"
              name="ageRangeEn"
              value={productForm.ageRangeEn || ''}
              onChange={handleFormChange}
              placeholder="e.g. 8-12 years"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ageRangeAr">Age Range (Arabic)</label>
            <input
              type="text"
              id="ageRangeAr"
              name="ageRangeAr"
              value={productForm.ageRangeAr || ''}
              onChange={handleFormChange}
              placeholder="e.g. 8-12 سنة"
              dir="rtl"
            />
          </div>

          {/* Video URL Field */}
          <div className="form-group">
            <label htmlFor="videoUrl">
              Video URL (YouTube, Vimeo, Facebook, or .mp4)
            </label>
            <input
              type="url"
              id="videoUrl"
              name="videoUrl"
              value={productForm.videoUrl || ''}
              onChange={handleFormChange}
              placeholder="https://youtube.com/watch?v=..."
              className={productForm.videoUrl && !isValidVideoUrl(productForm.videoUrl) ? 'input-error' : ''}
            />
            {productForm.videoUrl && !isValidVideoUrl(productForm.videoUrl) && (
              <div className="input-error-message">
                Please enter a valid video URL (YouTube, Vimeo, Facebook, or direct MP4 link)
              </div>
            )}
          </div>

          <div className="form-group featured-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="featured"
                checked={productForm.featured || false}
                onChange={(e) => handleFormChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: 'featured',
                    value: e.target.checked.toString()
                  }
                } as React.ChangeEvent<HTMLInputElement>)}
              />
              Featured Product
            </label>
          </div>
        </div>

        <div className="form-group full-width description-container">
          <label htmlFor="descriptionEn">
            Description (English)

          </label>
          <textarea
            id="descriptionEn"
            name="descriptionEn"
            value={productForm.descriptionEn || ''}
            onChange={handleFormChange}
            rows={8}
            placeholder="You can use HTML tags for formatting. Click the HTML Formatting button for help."
          ></textarea>

        </div><div className="form-group full-width description-container">
          <label htmlFor="descriptionAr">
            Description (Arabic)
            <button
              type="button"
              className="html-help-button"
              onClick={toggleHtmlInfo}
              title="HTML formatting help"
            >
              <span role="img" aria-label="HTML help">ℹ️</span> HTML Formatting
            </button>
          </label>
          <textarea
            id="descriptionAr"
            name="descriptionAr"
            value={productForm.descriptionAr || ''}
            onChange={handleFormChange}
            rows={8}
            dir="rtl"
            placeholder="يمكنك استخدام علامات HTML للتنسيق. انقر على زر تنسيق HTML للحصول على المساعدة."
          ></textarea>
        </div>

        <div className="form-group full-width">
          <div className="section-header">
            <label>Product Images *</label>
            <div className="section-subtitle">
              You can add up to 10 images. Click "Set as Main" to select the primary product image.
            </div>
          </div>

          {/* Only show the URL input form if less than 10 images */}
          {productImages.length < 10 && (
            <div className="image-upload-container">
              <div className="image-url-form">
                <input
                  type="url"
                  placeholder="Image URL (e.g., https://example.com/image.jpg)"
                  value={newImageUrl}
                  onChange={e => setNewImageUrl(e.target.value)}
                  className={newImageUrl && !isValidImageUrl(newImageUrl) ? 'input-error' : ''}
                />
                <input
                  type="text"
                  placeholder="Image description"
                  value={newImageAlt}
                  onChange={e => setNewImageAlt(e.target.value)}
                />
                <button
                  type="button"
                  className="add-image-button"
                  onClick={handleAddImageUrl}
                  disabled={!newImageUrl || !isValidImageUrl(newImageUrl) || productImages.length >= 10}
                >
                  + Add Image
                </button>
              </div>
              {newImageUrl && !isValidImageUrl(newImageUrl) && (
                <div className="input-error-message">
                  Please enter a valid image URL (must end with .jpg, .png, .gif, etc.)
                </div>
              )}
            </div>
          )}

          {productImages.length === 0 && (
            <div className="no-images-message">No images added. Please add at least one image.</div>
          )}

          <div className="product-images">
            {productImages.map((image, index) => (
              <div key={image.id} className={`image-item ${image.isMain ? 'main-image' : ''}`}>
                <div className="image-preview">
                  <img src={image.url} alt={image.alt} />
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={() => handleRemoveImage(index)}
                  >
                    ×
                  </button>

                  {/* Main image badge */}
                  {image.isMain && (
                    <div className="main-image-badge">Main</div>
                  )}
                </div>

                <div className="image-fields">
                  <div className="form-group">
                    <label>Alt Text</label>
                    <input
                      type="text"
                      value={image.alt}
                      onChange={e => handleUpdateImage(index, 'alt', e.target.value)}
                      placeholder="Image description"
                    />
                  </div>

                  {/* Set as main image button */}
                  {!image.isMain && (
                    <button
                      type="button"
                      className="set-main-button"
                      onClick={() => setMainImage(index)}
                    >
                      Set as Main
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* HTML Preview */}
        {(productForm.descriptionEn || productForm.descriptionAr) && (
          <div className="description-preview-container">
            <h4>Description Preview</h4>
            <div className="description-preview-tabs">
              <button
                type="button"
                className={`preview-tab ${!productForm.descriptionAr ? 'active' : ''}`}
                onClick={() => document.getElementById('en-preview')?.scrollIntoView({ behavior: 'smooth' })}
              >
                English
              </button>
              {productForm.descriptionAr && (
                <button
                  type="button"
                  className="preview-tab"
                  onClick={() => document.getElementById('ar-preview')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Arabic
                </button>
              )}
            </div>

            {productForm.descriptionEn && (
              <div id="en-preview" className="preview-panel">
                <h5>English Description Preview:</h5>
                <div
                  className="html-preview"
                  dangerouslySetInnerHTML={{ __html: productForm.descriptionEn }}
                />
              </div>
            )}

            {productForm.descriptionAr && (
              <div id="ar-preview" className="preview-panel">
                <h5>Arabic Description Preview:</h5>
                <div
                  className="html-preview"
                  dir="rtl"
                  dangerouslySetInnerHTML={{ __html: productForm.descriptionAr }}
                />
              </div>
            )}
          </div>
        )}

        {/* Video Preview */}
        {productForm.videoUrl && isValidVideoUrl(productForm.videoUrl) && (
          <div className="video-preview-container">
            <h4>Video Preview</h4>
            <div className="video-preview">
              {productForm.videoUrl.match(/\.(mp4|webm|ogg)$/i) ? (
                <video 
                  src={productForm.videoUrl} 
                  controls 
                  width="320"
                  height="180"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="iframe-container">
                  <iframe
                    src={
                      productForm.videoUrl.includes('youtube.com') || productForm.videoUrl.includes('youtu.be')
                        ? productForm.videoUrl.replace('watch?v=', 'embed/')
                        : productForm.videoUrl.includes('vimeo.com')
                        ? productForm.videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')
                        : productForm.videoUrl
                    }
                    title="Video Preview"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProductForm;